import { reactive } from 'vue'
import { api, ApiError } from '../api.js'

export const initialColorMappings = Object.freeze(['#16A564', '#1477E6', '#EE7700', '#8A2BE2', '#DB3E64', '#0C8D95']
  .map((color, index) => Object.freeze({ entityType: 'FUEL_GRADE', entityId: index + 1, color })))

export const colorMappingsState = reactive({ version: 0, mappings: [], canEdit: false,
  loading: false, loaded: false, error: null })
let scope = '', generation = 0, pending = null, controller = null, lastAttempt = 0, writing = false

export function validColorMappingId(value) {
  return (typeof value === 'number' || (typeof value === 'string' && /^[1-9]\d*$/.test(value)))
    && Number.isInteger(Number(value)) && Number(value) >= 1 && Number(value) <= 2147483647
}

export function normalizeHexColor(value) {
  return typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value) ? value.toUpperCase() : null
}

export function validateColorMappings(mappings) {
  if (!Array.isArray(mappings) || mappings.length > 256) throw invalid()
  const ids = new Set()
  return mappings.map((item) => {
    if (!item || Object.keys(item).some((key) => !['entityType', 'entityId', 'color'].includes(key))
      || item.entityType !== 'FUEL_GRADE' || typeof item.entityId !== 'number'
      || !validColorMappingId(item.entityId) || !normalizeHexColor(item.color) || ids.has(item.entityId)) throw invalid()
    ids.add(item.entityId)
    return { entityType: item.entityType, entityId: item.entityId, color: normalizeHexColor(item.color) }
  }).sort((a, b) => a.entityId - b.entityId)
}

function invalid() { return new ApiError('Invalid color mappings', 400, 'pts2_color_mappings_invalid') }
function snapshot(value) {
  if (!value || !Number.isSafeInteger(value.version) || value.version < 0 || typeof value.canEdit !== 'boolean') throw invalid()
  return { version: value.version, mappings: validateColorMappings(value.mappings), canEdit: value.canEdit }
}

export function setColorMappingScope(value) {
  if (value === scope) return
  scope = value; generation++; controller?.abort(); controller = null; pending = null; lastAttempt = 0; writing = false
  Object.assign(colorMappingsState, { version: 0, mappings: [], canEdit: false, loading: false, loaded: false, error: null })
}

export function loadColorMappings({ force = false } = {}) {
  if (pending) return pending
  if (colorMappingsState.loaded && !force) return Promise.resolve(colorMappingsState)
  const token = ++generation, request = new AbortController()
  controller = request; lastAttempt = Date.now()
  colorMappingsState.loading = true; colorMappingsState.error = null
  pending = api('/pts2/color-mappings', { signal: request.signal }).then((value) => {
    if (token !== generation) return null
    const result = snapshot(value)
    Object.assign(colorMappingsState, result, { loaded: true })
    return result
  }).catch((error) => {
    if (token !== generation) return null
    Object.assign(colorMappingsState, { mappings: [], loaded: false, canEdit: false, error })
    throw error
  }).finally(() => {
    if (token === generation) { colorMappingsState.loading = false; pending = null; controller = null }
  })
  return pending
}

export function refreshColorMappingsOnFocus() {
  if (Date.now() - lastAttempt >= 60000) return loadColorMappings({ force: true })
  return Promise.resolve(null)
}

export function saveColorMappings(mappings, version = colorMappingsState.version) {
  const entries = validateColorMappings(mappings)
  if (!Number.isSafeInteger(version) || version < 0) throw invalid()
  if (writing) return Promise.reject(new ApiError('Save already in progress', 409, 'pts2_color_mappings_busy'))
  controller?.abort(); pending = null
  const token = ++generation, request = new AbortController()
  controller = request; writing = true; lastAttempt = Date.now()
  pending = api('/pts2/color-mappings', { method: 'PUT', signal: request.signal,
    body: JSON.stringify({ version, mappings: entries }) }).then((value) => {
    if (token !== generation) return null
    const result = snapshot(value)
    Object.assign(colorMappingsState, result, { loaded: true, error: null })
    lastAttempt = Date.now()
    return result
  }).finally(() => {
    if (token === generation) { controller = null; colorMappingsState.loading = false; pending = null; writing = false }
  })
  return pending
}
